import type {UseDisclosureProps} from '@chakra-ui/react';
import {Collapse, useDisclosure} from '@chakra-ui/react';
import type {PropsOf} from '@chakra-ui/system';
import {forwardRef} from '@chakra-ui/system';
import {ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import {Text} from '@linx-security/ui';
import {dataAttr} from '@utils';
import type {FC} from 'react';
import {Children, isValidElement, useMemo} from 'react';

import type {NavMenuItem} from './Item';
import {NewNavMenuItem} from './NewItem';
import {useStyles} from './context';

export type Props = {
  isOpen?: boolean;
  isActive?: boolean;
  isCollapsible?: boolean;
  isTooltipDisabled?: boolean;
  key: string;
} & PropsOf<typeof NavMenuItem> &
  UseDisclosureProps;

export const NewNavMenuGroup: FC<Props> = forwardRef(
  (
    {
      isOpen: isOpenProp,
      onClose: onCloseProp,
      onOpen: onOpenProp,
      isCollapsible = true,
      isTooltipDisabled = false,
      defaultIsOpen,
      id,
      isActive,
      children,
      ...props
    },
    ref,
  ) => {
    const styles = useStyles();
    const {isOpen, getButtonProps} = useDisclosure({
      defaultIsOpen,
      onOpen: onOpenProp,
      onClose: onCloseProp,
      id,
      isOpen: isOpenProp,
    });

    const descendantIsActive = useMemo(() => {
      return Children.toArray(children).some(child => {
        return isValidElement(child) && (child as any).props.isActive;
      });
    }, [children]);

    return (
      <>
        <NewNavMenuItem
          role={`group`}
          {...props}
          level={0}
          isCollapsible={isCollapsible}
          isExpended={isCollapsible && isOpen}
          isTooltipDisabled={isTooltipDisabled}
          isActive={isActive}
          data-active-descendant={dataAttr(descendantIsActive)}
          aria-activedescendant={descendantIsActive ? props.id : undefined}
          {...getButtonProps()}>
          <Text sx={{opacity: !isCollapsible ? 0 : 1, transition: `opacity 0.2s ease-in-out`}}>{props.label}</Text>
        </NewNavMenuItem>
        <Collapse
          in={isCollapsible && isOpen}
          animateOpacity
          data-active={dataAttr(isActive)}
          style={{display: `flex`, alignSelf: `stretch`, flexDirection: `column`}}
          transition={{enter: [0.4, 0, 0.2, 1], exit: [0, 0, 0.2, 1]}}>
          {/* // display flex in collapse doesnt play nicely */}
          <ChakraDiv
            __css={styles[`menu-group`]}
            data-active={dataAttr(isActive)}
            data-open={dataAttr(isOpen)}
            role={`group`}
            ref={ref}>
            {children}
          </ChakraDiv>
        </Collapse>
      </>
    );
  },
);

export type {Props as NavGroupProps};
